import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Checkbox, List, ListItem, ListItemText, TextField, Typography,
} from '@mui/material';

const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Email every active technician with no home address on file a link to a
 * form where they enter it (no sign-in needed). Shows who will get it first;
 * the send is confirmed with that exact count.
 */
const RequestAddressesDialog = ({ open, onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [force, setForce] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${apiBase}/api/technicians/address-requests`, { params: force ? { force: '1' } : {}, headers: authHeaders() })
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Could not load the technicians.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, force]);

  const handleClose = () => {
    if (sending) return;
    setPreview(null);
    setForce(false);
    setMessage('');
    setError('');
    onClose();
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await axios.post(`${apiBase}/api/technicians/address-requests`,
        { expectedCount: preview.toSend, force, message: message.trim() || undefined },
        { headers: authHeaders() });
      onSent?.(res.data.queued);
      setSending(false);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the emails.');
      if (err.response?.status === 409) setPreview((p) => p && { ...p, toSend: err.response.data.toSend });
      setSending(false);
    }
  };

  const count = preview?.toSend || 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request missing addresses</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && preview && (
          <>
            <Typography gutterBottom>
              <strong>{preview.missingAddress}</strong> active technician(s) have no home address on file.
              {' '}{count ? <>An email with a link to a short address form will go to <strong>{count}</strong> of them.</> : 'Nobody to email.'}
            </Typography>
            {preview.recentlyRequested > 0 && (
              <FormControlLabel
                control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
                label={`Also email the ${preview.recentlyRequested} already asked in the last ${preview.resendAfterDays} days`}
              />
            )}
            <TextField
              label="Personal message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: 1000 }}
              sx={{ my: 2 }}
            />
            {count > 0 && (
              <List dense sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {preview.technicians.map((t) => (
                  <ListItem key={t.maidProfileId}>
                    <ListItemText
                      primary={`${t.firstName || ''} ${t.lastName || ''}`.trim() || t.email}
                      secondary={t.lastRequestedAt ? `${t.email} · asked ${new Date(t.lastRequestedAt).toLocaleDateString()}` : t.email}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Emails go out in the background (about 20 per minute). The link works for 30 days.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loading || sending || !count}
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {sending ? 'Sending…' : `Send ${count} email${count === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestAddressesDialog;
